import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import preAuthGQL from "../../gql/preAuth";
import AppTable from "../../shared/AppTable";
import AppButton from "../../shared/AppButton";
import AppModal from "../../components/AppModal";
import StatusOutcomePill from "../../shared/StatusOutcomePill";
import Pagination from "../../components/Pagination";
import { useTranslation } from "react-i18next";
import {
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  AutoMode as AutoModeIcon,
  AutoAwesome as AutoAwesomeIcon,
  ErrorOutline as ErrorIcon,
  Psychology as AiBrainIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Healing as HealingIcon,
  Medication as MedicationIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  ArrowForward as ArrowRightIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  HourglassEmpty as HourglassIcon,
  MedicalServices as MedicalIcon,
  Person as PersonIcon,
  QuestionAnswer as DoctorIcon,
  AssignmentTurnedIn as AssignmentDoneIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";

// Initial Demo Dataset incorporating PDF Specification for Pre-Authorization Approval Agent
const INITIAL_PREAUTH_DATA = [
  {
    id: "AUTH-928372",
    transaction_id: "AUTH-928372",
    pre_auth_ref_no: "REF-238921",
    patient_name: "Ahmed Ali",
    document_id: "MRN-483921",
    payer_name: "Bupa Arabia for Cooperative Insurance",
    request_procedure: "MRI Brain (CPT-70551)",
    encounter_type: "Outpatient",
    total_net: 1850.0,
    approval_limit: 2500.0,
    outcome: "Exception Error",
    status: "validation_error",
    operational_state: "NEEDS HUMAN REVIEW",
    sla_countdown: "01:42 remaining",
    clinical_alignment: 94,
    evidence_completeness: 87,
    missing_items: "Neurological examination findings missing",
    recommended_action: "Ask Doctor / Reconsider",
    validity_period: "12–20 Aug 2026",
    errors: [
      {
        error_code: "PREAUTH_EVIDENCE_MISSING",
        error_message: "Neurological examination findings missing in supportingInfo",
      },
    ],
  },
  {
    id: "AUTH-928373",
    transaction_id: "AUTH-928373",
    pre_auth_ref_no: "REF-238922",
    patient_name: "Tariq Al-Mansoor",
    document_id: "MRN-483922",
    payer_name: "Tawuniya Insurance",
    request_procedure: "CT Chest Contrast (CPT-71260)",
    encounter_type: "Inpatient",
    total_net: 3200.0,
    approval_limit: 4000.0,
    outcome: "Error",
    status: "validation_error",
    operational_state: "ATTENTION REQUIRED",
    sla_countdown: "00:55 remaining",
    clinical_alignment: 91,
    evidence_completeness: 78,
    missing_items: "Authorization reference REF-000000 mismatch with submitted service",
    recommended_action: "Remap Pre-Auth Reference",
    validity_period: "10–18 Aug 2026",
    errors: [
      {
        error_code: "AUTH_001",
        error_message: "Authorization reference REF-000000 mismatch with submitted service",
      },
    ],
  },
  {
    id: "AUTH-928374",
    transaction_id: "AUTH-928374",
    pre_auth_ref_no: "REF-238923",
    patient_name: "Sara Ahmed",
    document_id: "MRN-592014",
    payer_name: "Medgulf Insurance",
    request_procedure: "Echocardiogram Complete (CPT-93306)",
    encounter_type: "Outpatient",
    total_net: 1450.0,
    approval_limit: 2000.0,
    outcome: "Error",
    status: "validation_error",
    operational_state: "AUTONOMOUS",
    sla_countdown: "03:10 remaining",
    clinical_alignment: 96,
    evidence_completeness: 92,
    missing_items: "Unmapped local hospital service code SRV-9999",
    recommended_action: "Remap NPHIES Tariff Code",
    validity_period: "14–22 Aug 2026",
    errors: [
      {
        error_code: "XYZ-123",
        error_message: "Service code SRV-9999 mapping mismatch with NPHIES tariff",
      },
    ],
  },
  {
    id: "AUTH-928375",
    transaction_id: "AUTH-928375",
    pre_auth_ref_no: "REF-238924",
    patient_name: "Fahad K. Al-Otaibi",
    document_id: "MRN-338291",
    payer_name: "Malath Cooperative",
    request_procedure: "Lumbar Spine Surgery (CPT-22612)",
    encounter_type: "Inpatient",
    total_net: 18900.0,
    approval_limit: 22000.0,
    outcome: "Rejected",
    status: "denied",
    operational_state: "ESCALATED",
    sla_countdown: "00:15 remaining",
    clinical_alignment: 82,
    evidence_completeness: 65,
    missing_items: "Conservative treatment & physiotherapy trial documentation missing",
    recommended_action: "Submit Reconsideration & EMR Recovery",
    validity_period: "01–15 Aug 2026",
    errors: [
      {
        error_code: "PAYER_DENIAL",
        error_message: "Insufficient conservative treatment & clinical documentation",
      },
    ],
  },
  {
    id: "AUTH-928376",
    transaction_id: "AUTH-928376",
    pre_auth_ref_no: "REF-238925",
    patient_name: "Mohammed Rashid",
    document_id: "MRN-774920",
    payer_name: "Bupa Arabia",
    request_procedure: "Knee Arthroplasty (CPT-27447)",
    encounter_type: "Inpatient",
    total_net: 15400.0,
    approval_limit: 18000.0,
    outcome: "pending",
    status: "submitted",
    operational_state: "AUTONOMOUS",
    sla_countdown: "04:30 remaining",
    clinical_alignment: 98,
    evidence_completeness: 99,
    missing_items: "None (All clinical evidence attached)",
    recommended_action: "Awaiting Payer Disposition",
    validity_period: "15–30 Aug 2026",
    errors: [],
  },
];

const HARDCODED_PROMPTS = [
  {
    id: "p1",
    label: "⚡ Run Evidence Discovery & PACS Retrieval",
    query: "Run Evidence Discovery for missing radiology, consultation notes, and lab reports across active authorizations",
  },
  {
    id: "p2",
    label: "🩺 Execute CDSS Clinical Alignment Review",
    query: "Execute Clinical Decision Support System (CDSS) consistency review on diagnosis vs procedure",
  },
  {
    id: "p3",
    label: "📞 Trigger Automated Physician Outreach Query",
    query: "Prepare targeted physician queries for authorizations missing clinical examination findings",
  },
  {
    id: "p4",
    label: "📑 NPHIES Contractual Limit & Tariff Verification",
    query: "Verify Pre-Auth reference numbers, approval limits, validity windows, and tariff price codes",
  },
  {
    id: "p5",
    label: "🔄 Execute Denial Recovery & Reconsideration",
    query: "Build reconsideration packages for rejected pre-authorizations using EMR history",
  },
];

// Valid prompt keywords matching AI Pre-Auth capabilities
const VALID_PROMPT_KEYWORDS = [
  "evidence", "pacs", "cdss", "doctor", "physician", "outreach", "auth", "preauth",
  "authorization", "tariff", "limit", "denial", "reconsideration", "recovery",
  "radiology", "neurology", "mri", "ct", "verify", "audit", "submit"
];

// Helper: Parse error payload and generate precise Pre-Auth NPHIES Solution & Before -> After Delta Patch
const analyzePreAuthError = (claim) => {
  const errObj = claim.errors?.[0] || claim.validation_errors?.[0]?.errors?.[0];
  const errCode = errObj?.error_code || "PREAUTH_ERR";
  const errMsg = errObj?.error_message || claim.missing_items || "Validation Error";

  const lowerMsg = errMsg.toLowerCase();

  // Case 1: Missing Clinical Findings / Neuro Exam / Radiology
  if (lowerMsg.includes("neuro") || lowerMsg.includes("evidence") || lowerMsg.includes("exam") || lowerMsg.includes("findings")) {
    return {
      issue_category: "Missing Clinical Examination Findings",
      blocker_field: "clinical.supportingInfo.neurologicalExam",
      error_code: errCode,
      error_message: errMsg,
      before_val: "null (Missing Neurological Examination)",
      after_val: `Exam Document: neuro_exam_${claim.document_id || "483921"}_signed.pdf`,
      patch_action: `AI Approval Agent retrieved signed Neurological Examination from HIS EMR for patient '${claim.patient_name}' (MRN: ${claim.document_id}) and attached to Pre-Auth Request ${claim.transaction_id}.`,
      solution_type: "EMR Clinical Evidence Recovery",
      operational_state: "AUTONOMOUS",
    };
  }

  // Case 2: Authorization Reference Mismatch
  if (lowerMsg.includes("auth") || lowerMsg.includes("reference") || lowerMsg.includes("mismatch")) {
    return {
      issue_category: "Pre-Auth Reference Mismatch",
      blocker_field: "pre_auth_ref_no",
      error_code: errCode,
      error_message: errMsg,
      before_val: "REF-000000 (Unmatched Pre-Auth Ref)",
      after_val: `REF-${claim.document_id ? claim.document_id.slice(-6) : "238921"} (Matched Contract Ledger)`,
      patch_action: `AI Agent searched Payer Contract Ledger and updated reference number to REF-${claim.document_id ? claim.document_id.slice(-6) : "238921"}.`,
      solution_type: "Payer Contract Ledger Matching",
      operational_state: "AUTONOMOUS",
    };
  }

  // Case 3: Tariff Code Remapping
  if (lowerMsg.includes("tariff") || lowerMsg.includes("code") || lowerMsg.includes("srv")) {
    return {
      issue_category: "NPHIES Tariff Code Mismatch",
      blocker_field: "item.service_code",
      error_code: errCode,
      error_message: errMsg,
      before_val: "SRV-9999 (Unmapped Local HIS Code)",
      after_val: "CPT-70551 (Standard NPHIES Tariff)",
      patch_action: "AI remapped unlisted local hospital service code SRV-9999 to approved NPHIES CPT tariff code.",
      solution_type: "NPHIES Tariff Remapping",
      operational_state: "AUTONOMOUS",
    };
  }

  // Case 4: Conservative Treatment Denial Recovery
  if (lowerMsg.includes("denial") || lowerMsg.includes("conservative") || lowerMsg.includes("treatment")) {
    return {
      issue_category: "Payer Denial — Missing Trial Package",
      blocker_field: "reconsideration.evidencePackage",
      error_code: errCode,
      error_message: errMsg,
      before_val: "Package: Missing Conservative Trial",
      after_val: "Package: 14 EMR Physio & Medication Records Assembled",
      patch_action: `AI Agent extracted 6-week physiotherapy records and medication history from EMR for patient '${claim.patient_name}' to build reconsideration bundle.`,
      solution_type: "Automated Reconsideration Recovery",
      operational_state: "ATTENTION REQUIRED",
    };
  }

  // Generic Fallback
  return {
    issue_category: "NPHIES Pre-Auth Schema Validation",
    blocker_field: "PreAuth.item.validation",
    error_code: errCode,
    error_message: errMsg,
    before_val: "Invalid Pre-Auth Payload",
    after_val: "Validated & Auto-Corrected Payload",
    patch_action: `AI Agent applied self-healing schema patch for transaction ${claim.transaction_id}.`,
    solution_type: "Pre-Auth Auto-Correction",
    operational_state: "AUTONOMOUS",
  };
};

function PreAuthAiAgent() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("all");
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [doctorNotesInput, setDoctorNotesInput] = useState("");

  // Persistent Healed & Submitted Pre-Auths Ledger
  const [healedPreAuthsMap, setHealedPreAuthsMap] = useState({});

  // Pagination states
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);

  // Dynamic Live Agent Processing States
  const [isWorking, setIsWorking] = useState(false);
  const [activeClaimId, setActiveClaimId] = useState(null);
  const [currentStepText, setCurrentStepText] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalBatchSize, setTotalBatchSize] = useState(0);

  const [agentLogs, setAgentLogs] = useState([
    {
      id: 1,
      time: "16:22:40",
      type: "SYSTEM",
      text: "⚡ AI Insurance Approval Agent initialized. Model: Pre-Auth Operational Engine v4.2 Ready.",
    },
    {
      id: 2,
      time: "16:22:41",
      type: "CHECK",
      text: "✓ Checked patient history across 14 EMR clinical document sources.",
    },
  ]);

  // Construct dates for API requirement
  const currentDate = new Date();
  const fromDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1
  ).padStart(2, "0")}-01`;

  const toDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1
  ).padStart(2, "0")}-${new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate()}`;

  // Apollo Query
  const { data, loading, refetch } = useQuery(preAuthGQL.GET_PREAUTH_TRANSACTIONS, {
    variables: {
      input: {
        from_date: fromDate,
        to_date: toDate,
        limit: limit,
        page: pageNo,
      },
    },
    fetchPolicy: "network-only",
  });

  const [preAuthList, setPreAuthList] = useState(INITIAL_PREAUTH_DATA);

  // Sync API Data with Persistent Healed Map so fixes and pending states stay after changing page or refetching!
  useEffect(() => {
    if (data?.listPreAuthTransactions?.data?.length > 0) {
      const fetched = data.listPreAuthTransactions.data.map((c, idx) => {
        const id = c.pre_auth_id || c.transaction_id || `AUTH-${928372 + idx}`;
        const errorsArr = c.errors || [];
        const isErrored = errorsArr.length > 0 || c.outcome?.toLowerCase().includes("error") || c.outcome?.toLowerCase().includes("rejected");

        if (healedPreAuthsMap[id]) {
          return {
            id,
            transaction_id: id,
            pre_auth_ref_no: c.pre_auth_ref_no || `REF-${238921 + idx}`,
            patient_name: c.patient_name || "Patient Record",
            document_id: c.document_id || c.patient_document_id || `MRN-${483921 + idx}`,
            payer_name: c.payer_name || "Insurance Payer",
            request_procedure: c.type || "MRI Brain (CPT-70551)",
            encounter_type: "Outpatient",
            total_net: c.total_net || 1850.0,
            approval_limit: 2500.0,
            status: c.status || "",
            sla_countdown: "01:42 remaining",
            validity_period: "12–20 Aug 2026",
            errors: [],
            ...healedPreAuthsMap[id],
          };
        }

        return {
          id,
          transaction_id: id,
          pre_auth_ref_no: c.pre_auth_ref_no || `REF-${238921 + idx}`,
          patient_name: c.patient_name || "Patient Record",
          document_id: c.document_id || c.patient_document_id || `MRN-${483921 + idx}`,
          payer_name: c.payer_name || "Insurance Payer",
          request_procedure: c.type || "MRI Brain (CPT-70551)",
          encounter_type: "Outpatient",
          total_net: c.total_net || 1850.0,
          approval_limit: 2500.0,
          outcome: c.outcome || (isErrored ? "Error" : "pending"),
          status: c.status || "",
          operational_state: isErrored ? "NEEDS HUMAN REVIEW" : "AUTONOMOUS",
          sla_countdown: "01:42 remaining",
          clinical_alignment: isErrored ? 84 : 96,
          evidence_completeness: isErrored ? 78 : 98,
          missing_items: isErrored ? "Neurological examination findings missing" : "None",
          recommended_action: isErrored ? "Ask Doctor / Reconsider" : "Ready to Submit",
          validity_period: "12–20 Aug 2026",
          errors: errorsArr,
          ai_status: isErrored ? "Needs Evidence Discovery" : "Ready",
        };
      });
      setPreAuthList(fetched);
    } else {
      setPreAuthList((prev) =>
        prev.map((c) => (healedPreAuthsMap[c.id] ? { ...c, ...healedPreAuthsMap[c.id] } : c))
      );
    }

    if (data?.listPreAuthTransactions?.pages) setTotalPages(data.listPreAuthTransactions.pages);
    if (data?.listPreAuthTransactions?.total) setTotalEntries(data.listPreAuthTransactions.total);
  }, [data, healedPreAuthsMap]);

  // Handle Page Change
  const onPageChange = (newPage) => {
    setPageNo(newPage);
    refetch({
      input: {
        from_date: fromDate,
        to_date: toDate,
        limit: limit,
        page: newPage,
      },
    });
  };

  // Dynamic lookup for modal
  const selectedClaim = preAuthList.find((c) => c.id === selectedClaimId) || null;

  // DYNAMIC DEDICATED AI APPROVAL PIPELINE — Self-heals into "AI Self-Healed (Ready to Submit)"
  const runLiveAgentPipeline = (promptTitle, promptQuery) => {
    if (isWorking) return;

    const unhealed = preAuthList.filter(
      (c) =>
        (c.errors?.length > 0 ||
          c.outcome?.toLowerCase().includes("error") ||
          c.outcome?.toLowerCase().includes("rejected") ||
          c.ai_status === "Needs Evidence Discovery") &&
        !c.is_healed &&
        c.outcome !== "pending"
    );

    if (unhealed.length === 0) {
      toast.success("All pre-authorizations in current view have been self-healed and are ready for submission!");
      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          type: "SYSTEM",
          text: "ℹ️ ALL PRE-AUTHS HEALED: Zero errored authorizations remaining in current page. Ready for user submission.",
        },
        ...prev,
      ]);
      return;
    }

    const batch = unhealed;
    setTotalBatchSize(batch.length);

    setIsWorking(true);
    setProgressPercent(5);
    setProcessedCount(0);

    const timeStr = () => new Date().toLocaleTimeString();

    setAgentLogs((prev) => [
      {
        id: Date.now(),
        time: timeStr(),
        type: "ACTION",
        text: `🚀 EXECUTING APPROVAL AGENT: "${promptTitle}" — Processing ${batch.length} authorizations...`,
      },
      ...prev,
    ]);

    let index = 0;

    const processNextClaim = () => {
      if (index >= batch.length) {
        setIsWorking(false);
        setActiveClaimId(null);
        setCurrentStepText(`✨ AI Approval Pipeline Complete: ${batch.length} authorizations self-healed & ready for submission.`);
        setProgressPercent(100);

        setAgentLogs((prev) => [
          {
            id: Date.now(),
            time: timeStr(),
            type: "SUCCESS",
            text: `✨ BATCH SELF-HEALED: ${batch.length} Authorizations corrected! Click 'Submit Healed PreAuths' to send to NPHIES Payer Gateway.`,
          },
          ...prev,
        ]);

        toast.success(`✨ AI Approval Agent self-healed ${batch.length} pre-authorizations! Ready for submission.`);
        return;
      }

      const currentClaim = batch[index];
      const analysis = analyzePreAuthError(currentClaim);

      setActiveClaimId(currentClaim.id);
      setProcessedCount(index + 1);

      const pct = Math.round(((index + 0.3) / batch.length) * 100);
      setProgressPercent(pct);

      setCurrentStepText(
        `🧠 AI Analyzing authorization ${index + 1}/${batch.length}: ${currentClaim.id} (${currentClaim.patient_name}) — Diagnosing '${analysis.issue_category}'`
      );

      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: timeStr(),
          type: "ANALYSIS",
          text: `🧠 [AI Neural Check ${index + 1}/${batch.length}] Diagnosing Pre-Auth ${currentClaim.id}... Issue: '${analysis.issue_category}'. Target: ${analysis.blocker_field}`,
        },
        ...prev,
      ]);

      // Phase 2: Discovered Patch & Evidence Assembly
      setTimeout(() => {
        setCurrentStepText(
          `⚡ AI Self-Healing Delta Patch: [${analysis.before_val}] ➔ [${analysis.after_val}]`
        );

        setAgentLogs((prev) => [
          {
            id: Date.now(),
            time: timeStr(),
            type: "CHECK",
            text: `🔍 [EMR & PACS Evidence] Retrieved verified clinical evidence for Pre-Auth ${currentClaim.id}. Action: ${analysis.patch_action}`,
          },
          ...prev,
        ]);

        // Phase 3: Apply Patch & Update Status
        setTimeout(() => {
          const patchData = {
            outcome: "ready_to_submit",
            ai_status: "AI Self-Healed (Ready to Submit)",
            operational_state: "AUTONOMOUS",
            clinical_alignment: 96,
            evidence_completeness: 98,
            missing_items: "None (Clinical Evidence Assembled)",
            recommended_action: "Submit Pre-Auth Payload",
            patched_before: analysis.before_val,
            patched_after: analysis.after_val,
            patch_action: analysis.patch_action,
            blocker_field: analysis.blocker_field,
            is_healed: true,
            is_submitted: false,
            errors: [],
            // AI self-healing must preserve the original requested items.
            items: currentClaim.items,
          };

          setPreAuthList((prevList) =>
            prevList.map((c) => (c.id === currentClaim.id ? { ...c, ...patchData } : c))
          );

          setHealedPreAuthsMap((prevMap) => ({
            ...prevMap,
            [currentClaim.id]: patchData,
          }));

          setAgentLogs((prev) => [
            {
              id: Date.now(),
              time: timeStr(),
              type: "HEALING",
              text: `✨ [AI Self-Healed ${currentClaim.id}] Patch Applied: [${analysis.before_val}] ➔ [${analysis.after_val}]. Status set to 'Ready to Submit'.`,
            },
            ...prev,
          ]);

          index++;
          setTimeout(processNextClaim, 1000);
        }, 1400);
      }, 1500);
    };

    setTimeout(processNextClaim, 500);
  };

  // SUBMIT SINGLE PRE-AUTH TO NPHIES -> Changes Status to PENDING with AI Feedback
  const handleSubmitPreAuth = (claimId) => {
    const target = preAuthList.find((c) => c.id === claimId);
    if (!target) return;

    const submittedData = {
      outcome: "pending",
      status: "submitted",
      ai_status: "Submitted (Pending Payer Response)",
      operational_state: "AUTONOMOUS",
      is_submitted: true,
    };

    setPreAuthList((prevList) =>
      prevList.map((c) => (c.id === claimId ? { ...c, ...submittedData } : c))
    );

    setHealedPreAuthsMap((prevMap) => ({
      ...prevMap,
      [claimId]: {
        ...(prevMap[claimId] || {}),
        ...submittedData,
      },
    }));

    const timeStr = new Date().toLocaleTimeString();
    setAgentLogs((prev) => [
      {
        id: Date.now(),
        time: timeStr,
        type: "SYSTEM",
        text: `✨ SUBMITTED TO NPHIES GATEWAY: Pre-Auth ${target.id} (${target.patient_name}) sent to Payer Gateway. Status updated to PENDING.`,
      },
      ...prev,
    ]);

    toast.success(`✨ Pre-Auth ${target.id} submitted to NPHIES Gateway! Status set to Pending.`);
  };

  // SUBMIT ALL HEALED PRE-AUTHS IN BATCH TO NPHIES WITH ANIMATED AI PACING & STATUS UPDATES
  const handleBatchSubmitHealed = () => {
    const readyPreAuths = preAuthList.filter((c) => c.is_healed && !c.is_submitted);

    if (readyPreAuths.length === 0) {
      toast.error("No healed pre-authorizations waiting for submission. Run AI Agent self-healing first!");
      return;
    }

    setIsWorking(true);
    setTotalBatchSize(readyPreAuths.length);
    setProcessedCount(0);
    setProgressPercent(5);

    const timeStr = () => new Date().toLocaleTimeString();

    setAgentLogs((prev) => [
      {
        id: Date.now(),
        time: timeStr(),
        type: "ACTION",
        text: `🚀 EXECUTING NPHIES TRANSMISSION PIPELINE: Transmitting ${readyPreAuths.length} self-healed pre-authorizations to Payer Gateway...`,
      },
      ...prev,
    ]);

    let submitIdx = 0;

    const processNextSubmit = () => {
      if (submitIdx >= readyPreAuths.length) {
        setIsWorking(false);
        setActiveClaimId(null);
        setCurrentStepText(`✨ NPHIES Gateway Batch Transmission Complete: ${readyPreAuths.length} pre-authorizations updated to PENDING.`);
        setProgressPercent(100);

        setAgentLogs((prev) => [
          {
            id: Date.now(),
            time: timeStr(),
            type: "SUCCESS",
            text: `✨ BATCH SUBMITTED TO NPHIES: ${readyPreAuths.length} pre-authorizations transmitted. All statuses updated to PENDING.`,
          },
          ...prev,
        ]);

        toast.success(`✨ ${readyPreAuths.length} Pre-Authorizations submitted to NPHIES Gateway! All statuses set to Pending.`);
        return;
      }

      const targetAuth = readyPreAuths[submitIdx];
      setActiveClaimId(targetAuth.id);
      setProcessedCount(submitIdx + 1);

      const pct = Math.round(((submitIdx + 1) / readyPreAuths.length) * 100);
      setProgressPercent(pct);

      setCurrentStepText(
        `✨ Transmitting ${submitIdx + 1}/${readyPreAuths.length}: ${targetAuth.id} (${targetAuth.patient_name}) ➔ NPHIES Payer Gateway`
      );

      const submittedData = {
        outcome: "pending",
        status: "submitted",
        ai_status: "Submitted (Pending Payer Response)",
        operational_state: "AUTONOMOUS",
        is_submitted: true,
      };

      setPreAuthList((prevList) =>
        prevList.map((c) => (c.id === targetAuth.id ? { ...c, ...submittedData } : c))
      );

      setHealedPreAuthsMap((prevMap) => ({
        ...prevMap,
        [targetAuth.id]: {
          ...(prevMap[targetAuth.id] || {}),
          ...submittedData,
        },
      }));

      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: timeStr(),
          type: "SYSTEM",
          text: `✨ [NPHIES Gateway ${submitIdx + 1}/${readyPreAuths.length}] Transmitted Pre-Auth ${targetAuth.id} (${targetAuth.patient_name}). Status set to PENDING.`,
        },
        ...prev,
      ]);

      submitIdx++;
      setTimeout(processNextSubmit, 1500);
    };

    setTimeout(processNextSubmit, 500);
  };

  // Handle Doctor Communication Outreach
  const handleSendDoctorOutreach = (claimId) => {
    const target = preAuthList.find((c) => c.id === claimId);
    const notes = doctorNotesInput.trim() || "Please document relevant neurological findings to support requested MRI Brain authorization.";

    const timeStr = new Date().toLocaleTimeString();
    setAgentLogs((prev) => [
      {
        id: Date.now(),
        time: timeStr,
        type: "OUTREACH",
        text: `📞 PHYSICIAN OUTREACH SENT: Query dispatched to attending physician for Pre-Auth ${claimId}. Message: "${notes}"`,
      },
      ...prev,
    ]);

    toast.success(`Automated clinical inquiry sent to physician for Pre-Auth ${claimId}!`);
    setDoctorNotesInput("");
  };

  // Custom Prompt Form Submit
  const handleCustomPromptSubmit = (e) => {
    e.preventDefault();
    const inputVal = customPrompt.trim().toLowerCase();
    if (!inputVal) return;

    const isKeywordMatch = VALID_PROMPT_KEYWORDS.some((keyword) =>
      inputVal.includes(keyword)
    );

    if (!isKeywordMatch) {
      toast.error(
        "Unrecognized AI prompt. Please enter a valid pre-auth query (e.g. 'evidence discovery', 'doctor outreach', 'CDSS review').",
        { duration: 4000 }
      );
      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          type: "SYSTEM",
          text: `⚠️ UNRECOGNIZED PROMPT: "${customPrompt}". Supported capabilities: Evidence Discovery, CDSS Clinical Review, Doctor Outreach, NPHIES Limit Verification, Denial Recovery.`,
        },
        ...prev,
      ]);
      setCustomPrompt("");
      return;
    }

    runLiveAgentPipeline(`Custom Query: "${customPrompt}"`, customPrompt);
    setCustomPrompt("");
  };

  const filteredPreAuths = preAuthList.filter((c) => {
    const isErr = (c.errors?.length > 0 || c.outcome?.toLowerCase().includes("error") || c.outcome?.toLowerCase().includes("rejected")) && !c.is_healed && !c.is_submitted;
    if (activeTab === "nphies") return isErr;
    if (activeTab === "doctor") return c.operational_state === "NEEDS HUMAN REVIEW" || c.missing_items?.includes("missing");
    if (activeTab === "denial") return c.outcome?.toLowerCase().includes("rejected");
    if (activeTab === "ready") return c.is_healed || c.outcome === "pending" || c.is_submitted;
    return true;
  });

  const healedReadyCount = preAuthList.filter((c) => c.is_healed && !c.is_submitted).length;

  // Table Columns using system AppTable format
  const columns = [
    {
      header: t("Authorization ID / Ref"),
      field: "transaction_id",
      render: (row) => {
        const isCurrentActive = row.id === activeClaimId;
        return (
          <div className="flex items-center gap-1.5">
            {isCurrentActive && (
              <span className="w-2 h-2 rounded-full bg-sky-500 animate-ping" />
            )}
            <div>
              <span
                className={`font-semibold cursor-pointer hover:underline text-xs ${
                  isCurrentActive ? "text-sky-600 font-extrabold" : "text-primary"
                }`}
                onClick={() => {
                  setSelectedClaimId(row.id);
                  setModalOpen(true);
                }}
              >
                {row.id}
              </span>
              <div className="text-[10px] text-slate-400 font-mono">{row.pre_auth_ref_no}</div>
            </div>
          </div>
        );
      },
    },
    {
      header: t("Patient / MRN"),
      render: (row) => (
        <div>
          <div className="font-medium text-slate-800 text-xs">{row.patient_name || "---"}</div>
          <div className="text-[10px] text-slate-400 font-mono">{row.document_id || "---"}</div>
        </div>
      ),
    },
    {
      header: t("Payer Name"),
      render: (row) => (
        <span className="text-xs text-slate-700 block max-w-[160px] truncate" title={row.payer_name}>
          {row.payer_name || "---"}
        </span>
      ),
    },
    {
      header: t("CDSS Match"),
      align: "center",
      render: (row) => (
        <span className={`font-bold text-xs ${row.clinical_alignment >= 90 ? "text-emerald-600" : "text-amber-600"}`}>
          {row.clinical_alignment}%
        </span>
      ),
    },
    {
      header: t("Operational State"),
      render: (row) => {
        const state = row.operational_state || "AUTONOMOUS";
        return (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
              state === "AUTONOMOUS"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : state === "NEEDS HUMAN REVIEW" || state === "WAITING FOR HUMAN"
                ? "bg-amber-50 text-amber-800 border border-amber-200"
                : state === "ESCALATED"
                ? "bg-rose-50 text-rose-700 border border-rose-200"
                : "bg-blue-50 text-blue-700 border border-blue-200"
            }`}
          >
            {state}
          </span>
        );
      },
    },
    {
      header: t("Outcome Status"),
      render: (row) => {
        if (row.outcome === "ready_to_submit") {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-300">
              <CheckCircleIcon sx={{ fontSize: 12 }} /> AI Self-Healed
            </span>
          );
        }
        return <StatusOutcomePill value={row.outcome || "error"} />;
      },
    },
    {
      header: t("Self-Healing Delta (From ➔ To)"),
      render: (row) => {
        const analysis = analyzePreAuthError(row);
        const before = row.patched_before || analysis.before_val;
        const after = row.patched_after || analysis.after_val;
        const isFixed = row.is_healed || row.is_submitted || row.outcome === "pending";

        return (
          <div className="flex items-center gap-1 text-[10px] font-mono">
            <span className={isFixed ? "line-through text-slate-400" : "text-rose-600 font-bold"}>
              {before.length > 20 ? `${before.slice(0, 18)}...` : before}
            </span>
            <ArrowRightIcon sx={{ fontSize: 13 }} className="text-slate-400 shrink-0" />
            <span className={isFixed ? "text-emerald-700 font-bold bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200" : "text-slate-500"}>
              {after.length > 22 ? `${after.slice(0, 20)}...` : after}
            </span>
          </div>
        );
      },
    },
  ];

  // Custom Table Action triggers rendered in native AppTable Action bar
  const customActions = [
    {
      title: "Inspect & Review AI Agent",
      icon: <AutoModeIcon sx={{ fontSize: 18, color: "#0070C2" }} />,
      onClick: (row) => {
        setSelectedClaimId(row.id);
        setModalOpen(true);
      },
    },
    {
      title: "Submit Pre-Auth to NPHIES Gateway",
      icon: <CloudUploadIcon sx={{ fontSize: 18, color: "#10B981" }} />,
      shouldRender: (row) => row.is_healed && !row.is_submitted && row.outcome !== "pending",
      onClick: (row) => {
        handleSubmitPreAuth(row.id);
      },
    },
  ];

  // Dynamic Calculation of KPI Summary Statistics
  const pendingCount = preAuthList.filter((c) => c.outcome === "pending" || c.status === "submitted" || c.is_submitted).length;
  const doctorNeededCount = preAuthList.filter((c) => c.operational_state === "NEEDS HUMAN REVIEW" || c.operational_state === "WAITING FOR HUMAN" || c.missing_items?.toLowerCase().includes("missing")).length;
  const atRiskCount = preAuthList.filter((c) => (c.errors?.length > 0 || c.outcome?.toLowerCase().includes("error")) && !c.is_healed && !c.is_submitted).length;
  const recoveryCount = preAuthList.filter((c) => c.outcome?.toLowerCase().includes("rejected") || c.outcome?.toLowerCase().includes("denied")).length;
  const autoFixRatePercent = preAuthList.length > 0 ? Math.round((preAuthList.filter((c) => c.is_healed || c.is_submitted).length / preAuthList.length) * 100) : 100;

  // PDF Specification Command Center KPI Cards
  const netSummaryItems = [
    {
      label: t("Pending Requests"),
      value: pendingCount,
      textClassName: "text-amber-600 font-bold",
    },
    {
      label: t("AI Active Working"),
      value: isWorking ? `${processedCount}/${totalBatchSize}` : preAuthList.filter((c) => !c.is_healed && !c.is_submitted).length,
      textClassName: "text-sky-600 font-bold",
    },
    {
      label: t("Doctor Input Needed"),
      value: doctorNeededCount,
      textClassName: "text-rose-600 font-bold",
    },
    {
      label: t("At Risk"),
      value: atRiskCount,
      textClassName: "text-red-600 font-bold",
    },
    {
      label: t("Recovery Pipeline"),
      value: recoveryCount,
      textClassName: "text-indigo-600 font-bold",
    },
    {
      label: t("Self-Healed (Ready)"),
      value: healedReadyCount,
      textClassName: "text-emerald-600 font-bold",
    },
  ];

  return (
    <div className="pre_auth_container !p-0 space-y-2">
      {/* Title & Top Action Bar */}
      <div className="flex justify-between flex-col sm:flex-row gap-2 mb-1">
        <div className="w-full sm:w-[calc(100%-250px)]">
          <span className="font-semibold text-black/70 text-lg">
            {t("AI INSURANCE APPROVAL COMMAND CENTER")}
            <span className="text-lg ml-1">
              ({filteredPreAuths.length}{" "}
              <span className="text-xs font-bold italic">{t("Requests")}</span>)
            </span>
          </span>
        </div>
        <div className="flex gap-1.5 justify-end">
          <AppButton
            label={isWorking ? t("AI Working...") : t("Run Full Page AI Batch")}
            onClick={() => runLiveAgentPipeline("Full Page Pre-Auth Self-Healing", "Execute AI agent dynamically across all loaded pre-authorizations")}
            isLoading={isWorking}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          />
          <AppButton
            label={t(`Submit Healed PreAuths (${healedReadyCount})`)}
            onClick={handleBatchSubmitHealed}
            disabled={healedReadyCount === 0 || isWorking}
            startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
            className="!bg-emerald-600 hover:!bg-emerald-700 !text-white disabled:!opacity-50"
          />
          <AppButton
            label={t("Refetch API")}
            variant="outlined"
            onClick={() => refetch()}
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            className="!border-primary !text-primary hover:!bg-primary/10"
          />
        </div>
      </div>

      {/* Summary KPI Cards (PDF Specification) */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mb-1">
        {netSummaryItems.map((item) => (
          <div
            key={item.label}
            className="rounded-md bg-white p-2 shadow-sm border border-slate-200"
          >
            <div className="text-[10px] font-bold uppercase truncate text-slate-500">
              {item.label}
            </div>
            <div className={`text-[13px] font-bold mt-0.5 ${item.textClassName}`}>
              {typeof item.value === "number"
                ? item.value.toLocaleString()
                : item.value}
            </div>
          </div>
        ))}
      </div>

      {/* LIVE AGENT PROCESSING STATUS CARD */}
      <div className={`rounded-md border transition-all p-2.5 ${isWorking ? "bg-sky-50 border-sky-300 shadow-md" : "bg-white border-slate-200 shadow-sm"}`}>
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center gap-2">
            <AutoAwesomeIcon className={isWorking ? "text-sky-600 animate-pulse" : "text-primary"} sx={{ fontSize: 20 }} />
            <h3 className="text-xs font-bold text-slate-800 uppercase">
              {isWorking ? `Dynamic AI Approval Agent Processing (${processedCount}/${totalBatchSize} Items)...` : "Autonomous AI Approval State & Evidence Engine"}
            </h3>
          </div>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isWorking ? "bg-sky-200 text-sky-900 animate-pulse" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
            {isWorking ? `PROGRESS: ${progressPercent}%` : "SLA Optimal (98.4%)"}
          </span>
        </div>

        {/* Live Step Progress Description */}
        {isWorking && (
          <div className="mb-2 bg-white p-1.5 rounded border border-sky-200 text-xs font-semibold text-sky-900 flex items-center justify-between">
            <span className="truncate">👉 {currentStepText}</span>
            <span className="font-bold text-sky-700 shrink-0 ml-2">{processedCount}/{totalBatchSize} Fixed</span>
          </div>
        )}

        <LinearProgress variant="determinate" value={isWorking ? progressPercent : 100} className="rounded mb-2" style={{ height: 5 }} />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-center">
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">Approval Pass Rate</div>
            <div className="text-xs font-bold text-primary">96.8%</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">AI Auto-Fix Rate</div>
            <div className="text-xs font-bold text-indigo-600">{autoFixRatePercent}%</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">Doctor Response SLA</div>
            <div className="text-xs font-bold text-emerald-600">18 min</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">Payer Acceptance</div>
            <div className="text-xs font-bold text-blue-600">93.5%</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">Avg Turnaround</div>
            <div className="text-xs font-bold text-slate-700">12 min</div>
          </div>
        </div>
      </div>

      {/* Hardcoded AI Questions & Interactive Prompt Panel */}
      <div className="bg-white rounded-md border border-slate-200 p-2 shadow-sm">
        <div className="flex items-center gap-1.5 mb-1.5">
          <AiBrainIcon className="text-primary" sx={{ fontSize: 18 }} />
          <h3 className="text-xs font-bold text-slate-800 uppercase">
            Interactive AI Approval Agent Heuristics (Click Pre-Set Prompt to Run)
          </h3>
        </div>

        {/* Prompt Pills */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {HARDCODED_PROMPTS.map((p) => (
            <button
              key={p.id}
              onClick={() => runLiveAgentPipeline(p.label, p.query)}
              disabled={isWorking}
              className="text-[11px] bg-slate-50 hover:bg-sky-100 border border-slate-300 hover:border-primary text-slate-800 px-2.5 py-1 rounded font-semibold transition-all text-left flex items-center gap-1 disabled:opacity-50"
            >
              <span>{p.label}</span>
            </button>
          ))}
        </div>

        {/* Custom Prompt Form */}
        <form onSubmit={handleCustomPromptSubmit} className="flex gap-1.5">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-2 top-1.5 text-slate-400" fontSize="small" />
            <input
              type="text"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Type matching AI prompt (e.g. 'evidence discovery', 'doctor outreach', 'CDSS review')...."
              className="w-full bg-slate-50 border border-slate-300 rounded pl-8 pr-3 py-1 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:bg-white"
            />
          </div>
          <AppButton
            type="submit"
            label={t("Ask AI Agent")}
            isLoading={isWorking}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          />
        </form>
      </div>

      {/* Real-Time Agent Stream Console (PDF Time-Stamped Activity Panel) */}
      <div className="bg-slate-900 rounded-md border border-slate-800 shadow-sm overflow-hidden">
        <div className="bg-slate-950 px-2.5 py-1 border-b border-slate-800 flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${isWorking ? "bg-sky-400 animate-ping" : "bg-emerald-400"}`} />
            <span className="text-[10px] font-mono text-slate-300 font-bold uppercase tracking-wider">
              Real-Time AI Approval Agent Operational Console & Audit Log Stream {isWorking ? "(LIVE...)" : ""}
            </span>
          </div>
          <button
            onClick={() => setAgentLogs([])}
            className="text-[10px] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Clear Stream
          </button>
        </div>
        <div className="p-2 font-mono text-xs max-h-36 overflow-y-auto space-y-1 bg-slate-900 text-slate-200">
          {agentLogs.map((log) => (
            <div key={log.id} className="flex items-start gap-1.5 border-b border-slate-800/40 pb-0.5">
              <span className="text-slate-500 text-[10px] shrink-0">[{log.time}]</span>
              <span
                className={`font-bold shrink-0 text-[10px] ${
                  log.type === "ACTION"
                    ? "text-sky-400"
                    : log.type === "SUCCESS"
                    ? "text-emerald-400"
                    : log.type === "OUTREACH"
                    ? "text-purple-400"
                    : log.type === "CHECK"
                    ? "text-blue-300"
                    : log.type === "SYSTEM"
                    ? "text-amber-400"
                    : "text-slate-300"
                }`}
              >
                [{log.type}]
              </span>
              <span className="text-slate-300 text-[11px]">{log.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Priority Work Queue View using System AppTable */}
      <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden p-2">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-1.5">
          <div>
            <span className="font-semibold text-slate-800 text-base">
              {t("PRIORITY AUTHORIZATION WORK QUEUE")}
            </span>
          </div>
          <div className="flex gap-1 bg-slate-100 p-0.5 rounded border border-slate-200">
            {[
              { id: "all", label: "All Requests" },
              { id: "nphies", label: "Missing Evidence" },
              { id: "doctor", label: "Doctor Needed" },
              { id: "denial", label: "Payer Denial" },
              { id: "ready", label: "Self-Healed / Pending" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2 py-0.5 text-xs font-semibold rounded transition-all ${
                  activeTab === tab.id
                    ? "bg-white text-primary shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Native AppTable Component */}
        <AppTable
          cellClassName="!py-0"
          columns={columns}
          data={filteredPreAuths}
          loading={loading}
          actions={true}
          customActions={customActions}
          getRowSx={(row) =>
            row.id === activeClaimId
              ? { backgroundColor: "#e0f2fe !important", borderLeft: "4px solid #0284c7" }
              : {}
          }
        />

        {/* Server-Side Pagination Controls */}
        <div className="mt-2 border-t border-slate-200 pt-1">
          <Pagination
            currentPage={pageNo}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      {/* DETAILED 10-LAYER AI INSPECTION & APPROVAL MODAL (PDF Specification) */}
      {selectedClaim && (() => {
        const analysis = analyzePreAuthError(selectedClaim);
        const beforeVal = selectedClaim.patched_before || analysis.before_val;
        const afterVal = selectedClaim.patched_after || analysis.after_val;
        const patchAction = selectedClaim.patch_action || analysis.patch_action;

        const isSubmitted = selectedClaim.is_submitted || selectedClaim.outcome === "pending";
        const isHealed = selectedClaim.is_healed && !isSubmitted;

        return (
          <AppModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title={`AI Insurance Approval Agent Workbench — ${selectedClaim.id}`}
            maxWidth="lg"
            fullWidth={true}
          >
            <div className="space-y-3 text-xs">
              {/* LAYER 1: Patient + Request Context Header */}
              <div className="bg-slate-50 p-2.5 rounded border border-slate-200 flex justify-between items-center">
                <div>
                  <div className="text-sm font-bold text-slate-900">
                    Patient: {selectedClaim.patient_name} <span className="text-slate-400 font-mono">({selectedClaim.document_id})</span>
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    <strong>Request:</strong> {selectedClaim.request_procedure} | <strong>Payer:</strong> {selectedClaim.payer_name} | <strong>Encounter:</strong> {selectedClaim.encounter_type}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase text-slate-400">Active SLA</div>
                  <div className="text-xs font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                    ⏱️ {selectedClaim.sla_countdown || "01:42 remaining"}
                  </div>
                </div>
              </div>

              {/* LAYER 2: AI Status + Urgency State Banner */}
              <div className="flex justify-between items-center bg-sky-50 border border-sky-200 p-2 rounded">
                <div className="flex items-center gap-2">
                  <AutoModeIcon className="text-primary" fontSize="small" />
                  <span className="font-bold text-sky-900">Agent Operational State:</span>
                  <span className="bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded">
                    {selectedClaim.operational_state || "AUTONOMOUS"}
                  </span>
                </div>
                <Chip
                  label={
                    isSubmitted
                      ? "STATUS: SUBMITTED (PENDING PAYER RESPONSE)"
                      : isHealed
                      ? "STATUS: AI SELF-HEALED (READY FOR SUBMISSION)"
                      : `CDSS Alignment: ${selectedClaim.clinical_alignment || 94}%`
                  }
                  color={isSubmitted ? "warning" : isHealed ? "info" : "error"}
                  size="small"
                  className="font-bold"
                />
              </div>

              {/* LAYER 3: Case AI Analysis Matrix */}
              <div className="bg-white p-2.5 rounded border border-slate-200 space-y-2">
                <h4 className="font-bold text-xs text-slate-800 uppercase flex items-center gap-1">
                  <AiBrainIcon className="text-primary" fontSize="small" />
                  Case AI Analysis Matrix
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                  <div className="p-2 bg-slate-50 rounded border">
                    <div className="text-[10px] text-slate-500 font-semibold">Clinical Alignment</div>
                    <div className="text-sm font-bold text-emerald-600">{selectedClaim.clinical_alignment || 94}%</div>
                  </div>
                  <div className="p-2 bg-slate-50 rounded border">
                    <div className="text-[10px] text-slate-500 font-semibold">Evidence Completeness</div>
                    <div className="text-sm font-bold text-blue-600">{selectedClaim.evidence_completeness || 87}%</div>
                  </div>
                  <div className="p-2 bg-slate-50 rounded border">
                    <div className="text-[10px] text-slate-500 font-semibold">Missing Items</div>
                    <div className="text-xs font-bold text-rose-600 truncate">{selectedClaim.missing_items}</div>
                  </div>
                  <div className="p-2 bg-slate-50 rounded border">
                    <div className="text-[10px] text-slate-500 font-semibold">Recommended Action</div>
                    <div className="text-xs font-bold text-primary truncate">{selectedClaim.recommended_action}</div>
                  </div>
                </div>
              </div>

              {/* LAYER 4: Interactive AI Evidence Discovery Panel (PDF Specification) */}
              <div className="bg-white p-2.5 rounded border border-slate-200 space-y-1.5">
                <div className="flex justify-between items-center border-b pb-1">
                  <h4 className="font-bold text-xs text-slate-800 uppercase flex items-center gap-1">
                    <FileIcon className="text-purple-600" fontSize="small" />
                    Interactive AI Evidence Discovery Panel (HIS EMR & PACS Records)
                  </h4>
                  <span className="text-[10px] text-slate-400">Click item to view underlying source</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  <div className="p-1.5 bg-emerald-50/60 rounded border border-emerald-200 flex justify-between items-center cursor-pointer hover:bg-emerald-100/60">
                    <span className="font-medium text-emerald-900">✓ Neurology Consultation — 12 Aug 2026</span>
                    <span className="text-[10px] bg-emerald-200 text-emerald-800 px-1 py-0.2 rounded font-bold">HIS EMR</span>
                  </div>
                  <div className="p-1.5 bg-emerald-50/60 rounded border border-emerald-200 flex justify-between items-center cursor-pointer hover:bg-emerald-100/60">
                    <span className="font-medium text-emerald-900">✓ CT Brain — 10 Aug 2026</span>
                    <span className="text-[10px] bg-emerald-200 text-emerald-800 px-1 py-0.2 rounded font-bold">PACS DICOM</span>
                  </div>
                  <div className="p-1.5 bg-emerald-50/60 rounded border border-emerald-200 flex justify-between items-center cursor-pointer hover:bg-emerald-100/60">
                    <span className="font-medium text-emerald-900">✓ Medication History — 01–12 Aug 2026</span>
                    <span className="text-[10px] bg-emerald-200 text-emerald-800 px-1 py-0.2 rounded font-bold">Pharmacy</span>
                  </div>
                  <div className="p-1.5 bg-emerald-50/60 rounded border border-emerald-200 flex justify-between items-center cursor-pointer hover:bg-emerald-100/60">
                    <span className="font-medium text-emerald-900">✓ Persistent Headache Record — ED Note</span>
                    <span className="text-[10px] bg-emerald-200 text-emerald-800 px-1 py-0.2 rounded font-bold">Triage Note</span>
                  </div>
                </div>

                {/* Blocker Item callout */}
                <div className={`p-2 rounded border flex justify-between items-center ${isHealed || isSubmitted ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}>
                  <div className="flex items-center gap-2">
                    {isHealed || isSubmitted ? (
                      <CheckCircleIcon className="text-emerald-600" fontSize="small" />
                    ) : (
                      <ErrorIcon className="text-rose-600" fontSize="small" />
                    )}
                    <div>
                      <strong className={isHealed || isSubmitted ? "text-emerald-900" : "text-rose-900"}>
                        {isHealed || isSubmitted ? "✓ Neurological Examination Findings:" : "■ Neurological Examination Findings:"}
                      </strong>
                      <span className="ml-1 text-[11px] text-slate-700">
                        {isHealed || isSubmitted ? afterVal : "Not Found (Required Clinical Blocker)"}
                      </span>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${isHealed || isSubmitted ? "bg-emerald-200 text-emerald-900" : "bg-rose-200 text-rose-900"}`}>
                    {isHealed || isSubmitted ? "Resolved" : "Blocker"}
                  </span>
                </div>
              </div>

              {/* LAYER 5: Clinical Decision Support System (CDSS) Alignment & Flowchart */}
              <div className="bg-white p-2.5 rounded border border-slate-200 space-y-2">
                <h4 className="font-bold text-xs text-slate-800 uppercase flex items-center gap-1">
                  <HealingIcon className="text-indigo-600" fontSize="small" />
                  Clinical Decision Support System (CDSS) Visual Alignment
                </h4>

                <div className="bg-slate-900 text-slate-200 p-2.5 rounded font-mono text-[11px] space-y-1">
                  <div className="text-center font-bold text-sky-400">[ DIAGNOSIS: Persistent Intractable Headache ]</div>
                  <div className="text-center text-slate-500">↓ (94% Clinical Alignment Verified by CDSS Rule #CDSS-921)</div>
                  <div className="grid grid-cols-3 gap-2 text-center pt-1">
                    <div className="p-1 bg-slate-800 rounded border border-slate-700 text-emerald-400 font-bold">
                      ✓ MRI Brain (CPT-70551)
                    </div>
                    <div className="p-1 bg-slate-800 rounded border border-slate-700 text-emerald-400 font-bold">
                      ✓ Symptoms (ED Triage)
                    </div>
                    <div className="p-1 bg-slate-800 rounded border border-slate-700 text-emerald-400 font-bold">
                      ✓ Medication (Failed Trial)
                    </div>
                  </div>
                  <div className="text-center text-slate-500 pt-1">↓ Clinical Evidence Package Assembled (91% Score)</div>
                </div>

                <div className="text-[11px] space-y-0.5 text-slate-700">
                  <div>✓ Diagnosis supports requested MRI Brain service</div>
                  <div>✓ Symptoms support clinical diagnosis</div>
                  <div>✓ Previous CT investigation supports advanced imaging request</div>
                  <div className={isHealed || isSubmitted ? "text-emerald-700 font-bold" : "text-amber-700 font-bold"}>
                    {isHealed || isSubmitted ? "✓ All clinical elements verified & attached" : "■ One clinical element requires physician note (Neurological Exam)"}
                  </div>
                </div>
              </div>

              {/* LAYER 6: Automated Physician Outreach Panel */}
              {!isSubmitted && (
                <div className="bg-purple-50/70 p-2.5 rounded border border-purple-200 space-y-1.5">
                  <h4 className="font-bold text-xs text-purple-900 uppercase flex items-center gap-1">
                    <DoctorIcon className="text-purple-700" fontSize="small" />
                    Automated Physician Outreach Inquiry (AI ➔ PHYSICIAN)
                  </h4>
                  <div className="bg-white p-2 rounded border border-purple-200 text-xs text-slate-700 space-y-1 font-mono">
                    <div className="font-bold text-purple-900">[ AI ➔ ATTENDING PHYSICIAN ]</div>
                    <div>Authorization requires additional clinical evidence for requested procedure.</div>
                    <div className="text-emerald-700">✓ Already Discovered: Persistent headache, Failed conservative treatment, Previous CT, Neurology consultation.</div>
                    <div className="text-rose-700 font-bold">■ Missing: Neurological examination findings.</div>
                    <div>Please document relevant findings to support the requested MRI Brain authorization.</div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <input
                      type="text"
                      value={doctorNotesInput}
                      onChange={(e) => setDoctorNotesInput(e.target.value)}
                      placeholder="Add physician response note or click 'Send Request'..."
                      className="flex-1 bg-white border border-purple-300 rounded px-2 py-1 text-xs text-slate-800 focus:outline-none"
                    />
                    <AppButton
                      label={t("Send Doctor Request")}
                      onClick={() => handleSendDoctorOutreach(selectedClaim.id)}
                      startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                      className="!bg-purple-700 hover:!bg-purple-800"
                    />
                  </div>
                </div>
              )}

              {/* LAYER 7: Authorization Validation Engine Checklist */}
              <div className="bg-white p-2.5 rounded border border-slate-200 space-y-1">
                <h4 className="font-bold text-xs text-slate-800 uppercase flex items-center gap-1">
                  <VerifiedIcon className="text-emerald-600" fontSize="small" />
                  Authorization Validation Engine Checklist
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-slate-700 text-[11px]">
                  <div>✓ Authorization ID: <strong>{selectedClaim.id}</strong></div>
                  <div>✓ Reference ID: <strong>{selectedClaim.pre_auth_ref_no}</strong></div>
                  <div>✓ Patient MRN: <strong>{selectedClaim.document_id}</strong></div>
                  <div>✓ Procedure: <strong>{selectedClaim.request_procedure}</strong></div>
                  <div>✓ Approval Limit: <strong>SAR {selectedClaim.approval_limit}</strong></div>
                  <div>✓ Validity Window: <strong>{selectedClaim.validity_period}</strong></div>
                  <div>✓ Approved Qty: <strong>1</strong> | Requested: <strong>1</strong></div>
                  <div className="text-emerald-700 font-bold">✓ Contractual Rules: Passed</div>
                </div>
              </div>

              {/* LAYER 8 & 9: Decision Trace Log & End-to-End Approval Timeline */}
              <div className="bg-slate-900 text-slate-200 p-2.5 rounded font-mono text-[11px] space-y-1">
                <div className="text-amber-400 font-bold uppercase border-b border-slate-800 pb-1">
                  AI Decision Trace & Time-Stamped Audit Trail
                </div>
                <div className="text-slate-300 space-y-0.5">
                  <div>12 Aug 09:14 — Authorization created in HIS</div>
                  <div>12 Aug 09:15 — AI started autonomous analysis</div>
                  <div>12 Aug 09:17 — Evidence discovered across 4 EMR documents</div>
                  <div className="text-sky-300">12 Aug 09:20 — Physician query prepared</div>
                  <div className="text-emerald-400">12 Aug 12:43 — CDSS re-evaluated & Delta Patch generated</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <AppButton
                  label={t("Close")}
                  variant="outlined"
                  onClick={() => setModalOpen(false)}
                  className="!border-slate-300 !text-slate-700"
                />

                {!isHealed && !isSubmitted && (
                  <AppButton
                    label={t("Run AI Self-Healing")}
                    onClick={() => {
                      runLiveAgentPipeline("Single Pre-Auth Self-Healing", `Self-healing pre-auth ${selectedClaim.id}`);
                    }}
                    startIcon={<AutoModeIcon />}
                  />
                )}

                {isHealed && !isSubmitted && (
                  <AppButton
                    label={t("Submit Pre-Auth to NPHIES")}
                    onClick={() => {
                      handleSubmitPreAuth(selectedClaim.id);
                      setModalOpen(false);
                    }}
                    startIcon={<CloudUploadIcon />}
                    className="!bg-emerald-600 hover:!bg-emerald-700"
                  />
                )}
              </div>
            </div>
          </AppModal>
        );
      })()}
    </div>
  );
}

export default PreAuthAiAgent;
